import { useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Search, X, AlertCircle } from "lucide-react";
import { StockTakeFormData } from "@/pages/stock-take-create";
import { ScrollArea } from "@/components/ui/scroll-area";

interface InProgressStepProps {
  formData: StockTakeFormData;
  updateFormData: (updates: Partial<StockTakeFormData>) => void;
}

export function StockTakeInProgressStep({
  formData,
  updateFormData,
}: InProgressStepProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter items
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return formData.selectedItems;

    const query = searchQuery.toLowerCase();
    return formData.selectedItems.filter(
      (item) =>
        item.code.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
    );
  }, [formData.selectedItems, searchQuery]);

  // Calculate progress
  const progress = useMemo(() => {
    const counted = Object.keys(formData.countedQuantities).length;
    const total = formData.selectedItems.length;
    return total > 0 ? Math.round((counted / total) * 100) : 0;
  }, [formData.countedQuantities, formData.selectedItems]);

  // Update counted quantity
  const updateCountedQuantity = (code: string, value: string) => {
    const numValue = value === "" ? undefined : parseInt(value, 10);

    if (numValue !== undefined && (isNaN(numValue) || numValue < 0)) {
      return;
    }

    const newQuantities = { ...formData.countedQuantities };

    if (numValue === undefined) {
      delete newQuantities[code];
    } else {
      newQuantities[code] = numValue;
    }

    updateFormData({ countedQuantities: newQuantities });
  };

  // Check if item is counted
  const isItemCounted = (code: string) => {
    return formData.countedQuantities[code] !== undefined;
  };

  // Get uncounted items count
  const uncountedCount = formData.selectedItems.length - Object.keys(formData.countedQuantities).length;

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div>
        <h2 className="text-2xl font-semibold mb-1">Count Stock Items</h2>
        <p className="text-muted-foreground">
          Enter the counted quantity for each item
        </p>
      </div>

      {/* Progress Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium">Counting Progress</p>
            <p className="text-sm text-muted-foreground">
              {Object.keys(formData.countedQuantities).length} of{" "}
              {formData.selectedItems.length} items counted
            </p>
          </div>
          <Badge
            variant={progress === 100 ? "default" : "secondary"}
            className="text-lg px-4 py-1"
          >
            {progress}%
          </Badge>
        </div>
        <Progress value={progress} className="h-3" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Items Table */}
      <div className="rounded-lg border">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold text-right">
                  Expected Qty
                </TableHead>
                <TableHead className="font-semibold text-right w-[200px]">
                  Counted Qty <span className="text-destructive">*</span>
                </TableHead>
                <TableHead className="font-semibold text-center w-[100px]">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, index) => {
                const isCounted = isItemCounted(item.code);
                return (
                  <TableRow
                    key={item.code}
                    className={`${index % 2 === 0 ? "bg-muted/30" : ""} ${
                      isCounted ? "bg-green-50/50 dark:bg-green-900/10" : ""
                    }`}
                  >
                    <TableCell className="font-mono font-medium">
                      {item.code}
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.location}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.expectedQuantity}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        min="0"
                        value={
                          formData.countedQuantities[item.code] !== undefined
                            ? formData.countedQuantities[item.code]
                            : ""
                        }
                        onChange={(e) =>
                          updateCountedQuantity(item.code, e.target.value)
                        }
                        placeholder="Enter count"
                        className={`text-right font-mono ${
                          !isCounted ? "border-yellow-500 focus:border-yellow-600" : ""
                        }`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      {isCounted ? (
                        <Badge variant="default" className="bg-green-600">
                          Counted
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Validation Message */}
      {uncountedCount > 0 && (
        <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {uncountedCount} item{uncountedCount !== 1 ? "s" : ""} remaining
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Please enter counted quantities for all items before proceeding to the
                review step.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {progress === 100 && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                All items counted!
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                You can now proceed to the review step to verify the counts and complete
                the stock take.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
